import axios from 'axios';

// URL base de la API según el archivo REST
const API_URL = process.env.REACT_APP_API_URL || 'https://club-coca-cola-api.vercel.app/api/v1/back';

export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  profile: string;
}

export interface UpdateUserData {
  id: string;
  profile?: string;
  enabled?: boolean;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  dni: string | null;
  mobile: string | null;
  enabled: boolean;
  profile: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
}

// Obtener token de autenticación
export const getUserToken = (): string => {
  // Usar el token del archivo REST como fallback
  return localStorage.getItem('authToken') || 'FeMsA@2024';
};

// Obtener todos los usuarios
export const getUsers = async (): Promise<User[]> => {
  try {
    const token = getUserToken();
    const response = await axios.get<User[]>(
      `${API_URL}/users`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error al obtener usuarios:', error);
    throw new Error('No se pudieron cargar los usuarios');
  }
};

// Crear nuevo usuario
export const createUser = async (userData: CreateUserData): Promise<ApiResponse> => {
  try {
    const token = getUserToken();
    const response = await axios.post<ApiResponse>(
      `${API_URL}/users`,
      userData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al crear usuario:', error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || 'Error al crear el usuario');
    }
    throw new Error('Error al crear el usuario');
  }
};

// Actualizar usuario
export const updateUser = async (userData: UpdateUserData): Promise<ApiResponse> => {
  try {
    const token = getUserToken();
    const response = await axios.put<ApiResponse>(
      `${API_URL}/users`,
      userData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al actualizar usuario:', error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || 'Error al actualizar el usuario');
    }
    throw new Error('Error al actualizar el usuario');
  }
};

// Cambiar estado del usuario (habilitar/deshabilitar)
export const toggleUserStatus = async (userId: string, enabled: boolean): Promise<ApiResponse> => {
  try {
    const token = getUserToken();
    const response = await axios.put<ApiResponse>(
      `${API_URL}/users`,
      {
        id: userId,
        enabled: enabled
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al cambiar estado del usuario:', error);
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data?.message || 'Error al cambiar el estado del usuario');
    }
    throw new Error('Error al cambiar el estado del usuario');
  }
};

// Obtener usuarios activos (función de compatibilidad)
export const getActiveUsers = async (): Promise<User[]> => {
  try {
    const allUsers = await getUsers();
    return allUsers.filter(user => user.enabled);
  } catch (error) {
    console.error('Error al obtener usuarios activos:', error);
    return [];
  }
};
